import { useMemo, useCallback, useEffect, useState } from 'react'
import { motion } from 'framer-motion'
import { useTranslation } from 'react-i18next'
import { Product } from '../../stores/appReportsStore'
import SourcingSearch from './SourcingSearch'
import EditLeftProductCard from './edit/EditLeftProductCard'
import EditOptionProductCard from './edit/EditOptionProductCard'

interface Props {
  product: Product
  options: Product[]
  selectedOption: Product
  onCancel: () => void
  onSelect: (opt: Product) => void
  searchOptions: Product[]
  searchLoading: boolean
  onSearch: (term: string) => Promise<Product[]>
  onUpdateOptions: (opts: Product[]) => void
}

export default function SourcingEdit({
  product,
  options,
  selectedOption,
  onCancel,
  onSelect,
  searchOptions,
  searchLoading,
  onSearch,
  onUpdateOptions,
}: Props) {
  const { t } = useTranslation()
  const [sortOption, setSortOption] = useState('mostSavings')

  const parentUnitPrice = product.unitPrice
  const selectedIsPiece = ['stuk', 'piece'].includes(product.standardUnit)
  const selectedIsGml  = ['g', 'ml'].includes(product.standardUnit)

  /** sort: best savings → non-comparable → worst savings */
  const comparator = useCallback(
    (a: Product, b: Product) => {
      const aIsPiece = ['stuk','piece'].includes(a.standardUnit)
      const aIsGml   = ['g','ml'].includes(a.standardUnit)
      const bIsPiece = ['stuk','piece'].includes(b.standardUnit)
      const bIsGml   = ['g','ml'].includes(b.standardUnit)

      const aComp = (selectedIsPiece && aIsPiece) || (selectedIsGml && aIsGml)
      const bComp = (selectedIsPiece && bIsPiece) || (selectedIsGml && bIsGml)

      const diffA = aComp ? (a.unitPrice - parentUnitPrice) / parentUnitPrice * 100 : null
      const diffB = bComp ? (b.unitPrice - parentUnitPrice) / parentUnitPrice * 100 : null

      const grpA = diffA == null ? 1 : diffA <= 0 ? 0 : 2
      const grpB = diffB == null ? 1 : diffB <= 0 ? 0 : 2

      if (grpA !== grpB) return grpA - grpB         // comparability group
      if (grpA === 1)      return a.unitPrice - b.unitPrice // both non-comp: cheapest first
      return diffA! - diffB!                         // both comp: best % savings first
    },
    [selectedIsPiece, selectedIsGml, parentUnitPrice],
  )

  const sortedOptions = useMemo(() => [...options].sort(comparator), [options, comparator])

  /* always scroll to top when dialog mounts */
  useEffect(() => {
    const el = document.getElementById('mainContainer') as HTMLDivElement | null
    if (el) el.scrollTop = 0
  }, [])

  return (
    <div className="max-w-7xl mx-auto grid grid-cols-1 min-[1265px]:grid-cols-2 gap-6 mb-10 max-[1265px]:max-w-[600px]">
      {/* ① Parent product card – top on mobile, left column on desktop */}
      <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="order-1">
        <EditLeftProductCard product={product} />
        {/* desktop-only search */}
        <div className="hidden min-[1265px]:block">
          <h3 className="text-xl font-semibold mb-4 text-primary dark:text-primary-dark">
            {t('sourcing.searchProducts')}
          </h3>
          <SourcingSearch
            parentProduct={product}
            searchOptions={searchOptions}
            loading={searchLoading}
            onSearch={onSearch}
            onUpdateOptions={onUpdateOptions}
            onSelect={onSelect}
          />
        </div>
      </motion.div>

      {/* ② Option list – mobile middle, desktop right column */}
      <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="order-2">
        <div className="flex justify-between items-center mb-4">
          <h3 className="text-xl font-semibold text-primary dark:text-primary-dark">
            {t('sourcing.chooseOption')}
          </h3>

          <div className="flex items-center space-x-2">
            <span className="text-sm text-tertiary dark:text-tertiary-dark">
              {t('sourcing.sortBy')}
            </span>
            <select
              value={sortOption}
              onChange={e => setSortOption(e.target.value)}
              className="p-2 border border-borderGrey dark:border-borderGrey-dark rounded-lg bg-background dark:bg-background-dark text-sm font-medium"
            >
              <option value="mostSavings">{t('sourcing.mostSavings')}</option>
            </select>
          </div>
        </div>

        <div className="space-y-2">
          {sortedOptions.map(opt => {
            const highlight =
              opt.articleId === selectedOption?.articleId
                ? 'ring-1 ring-textGreen dark:ring-textGreen-dark'
                : ''
            return (
              <motion.div
                key={opt.articleId}
                layout
                onClick={() => onSelect(opt)}
                className={`flex items-center rounded-2xl bg-background dark:bg-background-dark cursor-pointer hover:bg-muted/5 dark:hover:bg-muted-dark/5 ${highlight}`}
              >
                <EditOptionProductCard parentProduct={product} product={opt} />
              </motion.div>
            )
          })}
        </div>
      </motion.div>

      {/* ③ Search – bottom on mobile, below product card (left col, second row) on desktop */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        className="order-3 min-[1265px]:hidden"
      >
        <h3 className="text-xl font-semibold mb-4 text-primary dark:text-primary-dark">
          {t('sourcing.searchProducts')}
        </h3>
        <SourcingSearch
          parentProduct={product}
          searchOptions={searchOptions}
          loading={searchLoading}
          onSearch={onSearch}
          onUpdateOptions={onUpdateOptions}
          onSelect={onSelect}
        />
      </motion.div>
    </div>
  )
}
